// src/SparepartsPages/Transit/CaseFileUpload.js
import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Button,
  Typography,
  TextField,
  Alert,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DataGrid } from "@mui/x-data-grid";
import Sidebar from "../Sidebar/Sidebar";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";
import { Redirect } from "react-router-dom";

// ✅ react-toastify v5.5.0
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* =========================
   Helpers
   ========================= */
const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const CaseFileUpload = () => {
  // ✅ NOT A HOOK (safe anywhere)
  const userToken = LocalstorageHelper.GetUserToken();

  /* =========================
     Hooks (MUST be unconditional)
     ========================= */
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [caseDate, setCaseDate] = useState(todayISO());

  const [rows, setRows] = useState([]);
  const [format, setFormat] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [totalQty, setTotalQty] = useState(0);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [warn, setWarn] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  const [serverData, setServerData] = useState(null);
  const [needsOverwrite, setNeedsOverwrite] = useState(false);

  const columns = useMemo(
    () => [
      { field: "casedate", headerName: "Case Date", width: 120 },
      { field: "dealercode", headerName: "Dealer Code", width: 140 },
      { field: "dealername", headerName: "Dealer Name", width: 260 },
      { field: "city", headerName: "City", width: 150 },
      { field: "invoicenumber", headerName: "Invoice No", width: 160 },
      { field: "deliverynumber", headerName: "Delivery No", width: 150 },
      { field: "casenumber", headerName: "Case/Box No", width: 170 },
      { field: "cartonbox", headerName: "Carton/Box To", width: 150 },
      { field: "partno", headerName: "Part No", width: 150 },
      { field: "partdescription", headerName: "Part Description", width: 360 },
      { field: "qty", headerName: "Qty", width: 90, type: "number" },
    ],
    []
  );

  const previewRows = useMemo(
    () => rows.map((r, i) => ({ id: i + 1, ...r })),
    [rows]
  );

  const serverTableCols = useMemo(() => {
    if (!serverData) return [];

    if (serverData.type === "missing") {
      return [
        { field: "partno", headerName: "Part No", width: 160 },
        { field: "invoicenumber", headerName: "Invoice No", width: 160 },
        { field: "itemname", headerName: "Item Name", width: 320 },
      ];
    }

    if (serverData.type === "mismatch") {
      return [
        { field: "invno", headerName: "Inv No", width: 160 },
        { field: "partno", headerName: "Part No", width: 160 },
        { field: "qty", headerName: "Transit Qty", width: 140, type: "number" },
        { field: "caseqty", headerName: "Case Qty", width: 140, type: "number" },
      ];
    }

    if (serverData.type === "block") {
      return [{ field: "invoicenumber", headerName: "Invoice No", width: 200 }];
    }

    if (serverData.type === "transitMissing") {
      return [{ field: "invoicenumber", headerName: "Invoice No", width: 200 }];
    }

    return [];
  }, [serverData]);

  const serverTableRows = useMemo(() => {
    if (!serverData) return [];
    const arr =
      serverData.data ||
      serverData.mimatchedData ||
      serverData.mismatchedData ||
      [];
    if (!Array.isArray(arr)) return [];
    return arr.map((r, i) => ({ id: i + 1, ...r }));
  }, [serverData]);

  /* =========================
     Functions
     ========================= */
  const clearAll = () => {
    setFile(null);
    setFileName("");
    setCaseDate(todayISO());
    setRows([]);
    setFormat("");
    setRowCount(0);
    setTotalQty(0);
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);
    setNeedsOverwrite(false);
    toast.info("Cleared.");
  };

  const safeReadJson = async (res) => {
    // ✅ Works even if backend accidentally sends text/html
    const raw = await res.text();
    try {
      return raw ? JSON.parse(raw) : {};
    } catch {
      return { type: "error", message: raw || "Request failed" };
    }
  };

  const callPreview = async (pickedFile, dateISO) => {
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);
    setNeedsOverwrite(false);

    if (!pickedFile) return;

    setLoadingPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);
      fd.append("caseDate", dateISO || "");

      const res = await fetch(`/casefile/transit/v2/preview`, {
        method: "POST",
        headers: { sptoken: userToken },
        body: fd,
      });

      const data = await safeReadJson(res);

      if (!res.ok || data?.type === "error") {
        throw new Error(data?.message || "Preview failed");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
      setFormat(data.format || "");
      setRowCount(Number(data.rowCount || 0));
      setTotalQty(Number(data.totalQty || 0));

      if (data.suggestedCaseDate) setCaseDate(data.suggestedCaseDate);

      const msg = `Preview ready. Format: ${data.format || "-"} | Rows: ${
        data.rowCount || 0
      }`;
      setInfo(msg);
      toast.success(msg);

      if (data.warning) {
        setWarn(data.warning);
        toast.warning(data.warning);
      }
    } catch (e) {
      setRows([]);
      setFormat("");
      setRowCount(0);
      setTotalQty(0);
      const msg = e?.message || "Preview failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleFilePick = async (picked) => {
    setFile(picked || null);
    setFileName(picked?.name || "");
    setRows([]);
    setFormat("");
    setRowCount(0);
    setTotalQty(0);
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);
    setNeedsOverwrite(false);

    if (picked) {
      toast.info("File selected. Generating preview...");
      await callPreview(picked, caseDate);
    }
  };

  const callImport = async ({ overwrite = false } = {}) => {
    setError("");
    setInfo("");
    setWarn("");
    setServerData(null);

    if (!file) {
      const msg = "Choose a file first.";
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!rows.length) {
      const msg = "No preview rows. Please Preview first.";
      setError(msg);
      toast.error(msg);
      return;
    }

    setLoadingImport(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caseDate", caseDate || "");
      fd.append("overwrite", overwrite ? "1" : "0");

      const res = await fetch(`/casefile/transit/v2/import`, {
        method: "POST",
        headers: { sptoken: userToken },
        body: fd,
      });

      const data = await safeReadJson(res);

      // ✅ handle server flows first (even if status is 400/500)
      if (data?.type === "overwrite") {
        setNeedsOverwrite(true);
        setServerData(data);
        const msg = data.message || "Invoice exists. Overwrite?";
        setWarn(msg);
        toast.warning(msg);
        return;
      }

      if (
        ["missing", "mismatch", "block", "transitMissing"].includes(data?.type)
      ) {
        setNeedsOverwrite(false);
        setServerData(data);
        const msg = data.message || "Import validation failed.";
        setError(msg);
        toast.error(msg);
        return;
      }

      if (!res.ok || data?.type === "error") {
        throw new Error(data?.message || "Import failed");
      }

      // ✅ success
      setNeedsOverwrite(false);
      setServerData(null);
      const msg = data.message || "Import Successful!";
      setInfo(msg);
      toast.success(msg);
    } catch (e) {
      const msg = e?.message || "Import failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoadingImport(false);
    }
  };

  /* =========================
     ✅ Redirect AFTER all hooks
     ========================= */
  if (!userToken) return <Redirect exact to="/sparepartsLogin" />;

  return (
    <div>
      {/* If you already have ToastContainer in App.js, remove this line */}
      <ToastContainer position="top-right" autoClose={2500} newestOnTop />

      <Sidebar
        Title={"CASE FILE TRANSIT (V2)"}
        expandedPanel={"panel4"}
        children={
          <Box sx={{ p: 2 }}>
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader
                title="CaseFile Upload & Import (V2)"
                subheader="New API | Company 1 = BAJAJ | Company 2 = TVS | Flexible header mapping supported"
              />
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      fullWidth
                      disabled={loadingPreview || loadingImport}
                    >
                      Choose File
                      <input
                        hidden
                        type="file"
                        accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        onChange={(e) => handleFilePick(e.target.files?.[0])}
                      />
                    </Button>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                      {fileName ? `Selected: ${fileName}` : "No file selected"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      label="Case Date (used if file has no date)"
                      type="date"
                      value={caseDate}
                      onChange={(e) => setCaseDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      disabled={loadingPreview || loadingImport}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      disabled={!file || loadingPreview || loadingImport}
                      onClick={() => callPreview(file, caseDate)}
                    >
                      {loadingPreview ? "Preview..." : "Preview"}
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent={{ xs: "flex-start", md: "flex-end" }}
                      flexWrap="wrap"
                    >
                      <Chip label={`Format: ${format || "-"}`} />
                      <Chip label={`Rows: ${rowCount || rows.length}`} />
                      <Chip label={`Total Qty: ${totalQty}`} />
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CloudUploadIcon />}
                        disabled={!file || !rows.length || loadingImport}
                        onClick={() => callImport({ overwrite: false })}
                      >
                        {loadingImport ? "Importing..." : "Import"}
                      </Button>

                      {needsOverwrite && (
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<WarningAmberIcon />}
                          disabled={!file || loadingImport}
                          onClick={() => callImport({ overwrite: true })}
                        >
                          {loadingImport ? "Overwriting..." : "Overwrite Import"}
                        </Button>
                      )}

                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        disabled={loadingPreview || loadingImport}
                        onClick={clearAll}
                      >
                        Clear
                      </Button>
                    </Stack>
                  </Grid>

                  {error && (
                    <Grid item xs={12}>
                      <Alert severity="error">{error}</Alert>
                    </Grid>
                  )}
                  {warn && (
                    <Grid item xs={12}>
                      <Alert severity="warning">{warn}</Alert>
                    </Grid>
                  )}
                  {info && (
                    <Grid item xs={12}>
                      <Alert severity="success">{info}</Alert>
                    </Grid>
                  )}
                </Grid>

                <Box sx={{ mt: 2, height: 520 }}>
                  <DataGrid
                    rows={previewRows}
                    columns={columns}
                    density="compact"
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 50, page: 0 } },
                    }}
                    sx={{
                      borderRadius: 2,
                      "& .MuiDataGrid-columnHeaders": { fontWeight: 800 },
                    }}
                  />
                </Box>

                {serverData && serverTableCols.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Server Validation Details
                    </Typography>
                    <Box sx={{ height: 360 }}>
                      <DataGrid
                        rows={serverTableRows}
                        columns={serverTableCols}
                        density="compact"
                        disableRowSelectionOnClick
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 50, page: 0 },
                          },
                        }}
                        sx={{
                          borderRadius: 2,
                          "& .MuiDataGrid-columnHeaders": { fontWeight: 800 },
                        }}
                      />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        }
      />
    </div>
  );
};

export default CaseFileUpload;
